/**
 * MetaConnectPanel – displayed in the marketing-analyst module
 * Shows connect/disconnect, ad-account selection, "Pobierz dane" button, and KPI dashboard.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { escapeHtml } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  DollarSign,
  Users,
  PhoneCall,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface AdAccount {
  id: string;
  name: string;
  accountStatus: number;
  currency: string;
}

interface MetaStatus {
  connected: boolean;
  metaUserName?: string;
  metaUserId?: string;
  selectedAdAccountId?: string;
  selectedAdAccountName?: string;
  adAccounts?: AdAccount[];
  tokenExpiresAt?: string;
  /** Konto reklamowe przypisane przez admina/agencje — user nie moze go zmienic. */
  assignedByAdmin?: boolean;
}

interface CampaignInsight {
  campaignId: string;
  campaignName: string;
  status: string;
  objective: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  conversions: string;
  costPerResult: string;
  results: string;
  leads: string;
  costPerLead: string;
  schedules: string;
  costPerSchedule: string;
  actions: { actionType: string; value: string }[];
  costPerActionType: { actionType: string; value: string }[];
  dateStart: string;
  dateStop: string;
}

interface MetaInsightsData {
  campaigns: CampaignInsight[];
  adSets: unknown[];
  context: string;
  adAccountId: string;
  adAccountName: string;
  datePreset: string;
}

interface MetaConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

function buildDatePresets(t: TFunction) {
  return [
    { value: "today", label: t("datePresets.today") },
    { value: "yesterday", label: t("datePresets.yesterday") },
    { value: "last_7d", label: t("datePresets.last_7d") },
    { value: "last_14d", label: t("datePresets.last_14d") },
    { value: "last_30d", label: t("datePresets.last_30d") },
    { value: "last_90d", label: t("datePresets.last_90d") },
    { value: "this_month", label: t("datePresets.this_month") },
    { value: "last_month", label: t("datePresets.last_month") },
  ];
}

function formatCurrency(value: number, currency = "PLN"): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(value);
}

// ---- KPI Card with target comparison ----
// kpiStatus: 'ok' = within target (green), 'bad' = outside target (red), null = no target set (default color)
function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
  kpiStatus,
  targetLabel,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
  kpiStatus?: "ok" | "bad" | null;
  targetLabel?: string;
}) {
  const colorMap: Record<string, { bg: string; text: string; border: string; iconBg: string }> = {
    blue: {
      bg: "bg-red-500/10",
      text: "text-blue-400",
      border: "border-red-500/20",
      iconBg: "bg-red-500/20",
    },
    green: {
      bg: "bg-emerald-500/10",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      iconBg: "bg-emerald-500/20",
    },
    purple: {
      bg: "bg-red-500/10",
      text: "text-red-400",
      border: "border-red-500/20",
      iconBg: "bg-red-500/20",
    },
    amber: {
      bg: "bg-amber-500/10",
      text: "text-amber-400",
      border: "border-amber-500/20",
      iconBg: "bg-amber-500/20",
    },
    rose: {
      bg: "bg-rose-500/10",
      text: "text-rose-400",
      border: "border-rose-500/20",
      iconBg: "bg-rose-500/20",
    },
  };

  // Override colors based on KPI status
  const statusOverrides: Record<
    string,
    { bg: string; text: string; border: string; iconBg: string }
  > = {
    ok: {
      bg: "bg-emerald-500/10",
      text: "text-emerald-400",
      border: "border-emerald-500/30",
      iconBg: "bg-emerald-500/20",
    },
    bad: {
      bg: "bg-red-500/10",
      text: "text-red-400",
      border: "border-red-500/30",
      iconBg: "bg-red-500/20",
    },
  };

  const c = kpiStatus ? statusOverrides[kpiStatus] : colorMap[color] || colorMap.blue;

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-3 flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        <div className={`${c.iconBg} rounded-lg p-1.5`}>
          <Icon className={`h-3.5 w-3.5 ${c.text}`} />
        </div>
        <span className="text-[11px] text-white/60 font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className={`text-xl font-bold ${c.text} mt-1`}>{value}</div>
      {targetLabel && (
        <span
          className={`text-[10px] font-medium ${kpiStatus === "ok" ? "text-emerald-500" : kpiStatus === "bad" ? "text-red-500" : "text-white/40"}`}
        >
          {kpiStatus === "ok" ? "" : kpiStatus === "bad" ? "" : "·"} {targetLabel}
        </span>
      )}
      {subtitle && <span className="text-[10px] text-white/40">{subtitle}</span>}
    </div>
  );
}

// ---- KPI targets shape ----
interface KpiTargets {
  kpiCostPerLead?: string | null;
  kpiCostPerCall?: string | null;
  kpiCostPerClient?: string | null;
  kpiTargetRoas?: string | null;
}

// Helper: compare actual cost vs target (lower = better for costs)
function costStatus(actual: number, target: string | null | undefined): "ok" | "bad" | null {
  if (!target || actual === 0) return null;
  const t = parseFloat(target);
  if (isNaN(t) || t === 0) return null;
  return actual <= t ? "ok" : "bad";
}

// ---- KPI Dashboard ----
function KpiDashboard({
  campaigns,
  currency,
  targets,
}: {
  campaigns: CampaignInsight[];
  currency: string;
  targets?: KpiTargets;
}) {
  const { t } = useTranslation("metaPanel");
  const [showCampaigns, setShowCampaigns] = useState(false);

  // Aggregate across all campaigns
  const totalSpend = campaigns.reduce((s, c) => s + parseFloat(c.spend || "0"), 0);
  const totalLeads = campaigns.reduce((s, c) => s + parseInt(c.leads || "0", 10), 0);
  const totalSchedules = campaigns.reduce((s, c) => s + parseInt(c.schedules || "0", 10), 0);
  const costPerLead = totalLeads > 0 ? totalSpend / totalLeads : 0;
  const costPerSchedule = totalSchedules > 0 ? totalSpend / totalSchedules : 0;

  const dateRange =
    campaigns.length > 0 ? `${campaigns[0].dateStart} – ${campaigns[0].dateStop}` : "";

  return (
    <div className="space-y-3 mt-3">
      {/* Date range subtitle */}
      {dateRange && (
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-white/[0.06]" />
          <span className="text-[10px] text-white/40 font-medium uppercase tracking-widest">
            {t("kpi.results", { dateRange })}
          </span>
          <div className="h-px flex-1 bg-white/[0.06]" />
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <KpiCard
          label={t("kpi.spend")}
          value={formatCurrency(totalSpend, currency)}
          icon={DollarSign}
          color="blue"
          subtitle={t("kpi.spendSubtitle")}
        />
        <KpiCard
          label={t("kpi.leads")}
          value={formatNumber(totalLeads)}
          icon={Users}
          color="green"
          subtitle={t("kpi.leadsSubtitle")}
        />
        <KpiCard
          label={t("kpi.costPerLead")}
          value={totalLeads > 0 ? formatCurrency(costPerLead, currency) : "—"}
          icon={Target}
          color="purple"
          subtitle={
            totalLeads > 0
              ? t("kpi.costPerLeadSubtitle", {
                  leads: totalLeads,
                  spend: formatCurrency(totalSpend, currency),
                })
              : t("kpi.noLeads")
          }
          kpiStatus={totalLeads > 0 ? costStatus(costPerLead, targets?.kpiCostPerLead) : null}
          targetLabel={
            targets?.kpiCostPerLead
              ? t("kpi.targetLabel", { label: `${targets.kpiCostPerLead} ${currency}` })
              : undefined
          }
        />
        <KpiCard
          label={t("kpi.calls")}
          value={formatNumber(totalSchedules)}
          icon={PhoneCall}
          color="amber"
          subtitle={t("kpi.callsSubtitle")}
        />
        <KpiCard
          label={t("kpi.costPerCall")}
          value={totalSchedules > 0 ? formatCurrency(costPerSchedule, currency) : "—"}
          icon={TrendingUp}
          color="rose"
          subtitle={
            totalSchedules > 0
              ? t("kpi.costPerCallSubtitle", {
                  count: totalSchedules,
                  spend: formatCurrency(totalSpend, currency),
                })
              : t("kpi.noCalls")
          }
          kpiStatus={
            totalSchedules > 0 ? costStatus(costPerSchedule, targets?.kpiCostPerCall) : null
          }
          targetLabel={
            targets?.kpiCostPerCall
              ? t("kpi.targetLabel", { label: `${targets.kpiCostPerCall} ${currency}` })
              : undefined
          }
        />
      </div>

      {/* Campaign Breakdown Toggle */}
      {campaigns.length > 0 && (
        <div>
          <button
            onClick={() => setShowCampaigns(!showCampaigns)}
            className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
          >
            {showCampaigns ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            <span className="font-medium">
              {t("kpi.campaignBreakdown", { count: campaigns.length })}
            </span>
          </button>

          {showCampaigns && (
            <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#0a0c10]/80 text-white/60">
                    <th className="text-left p-2 font-medium">{t("kpi.colCampaign")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colSpend")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colLeads")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colCostPerLead")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colCalls")}</th>
                    <th className="text-right p-2 font-medium">{t("kpi.colCostPerCall")}</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const spend = parseFloat(c.spend || "0");
                    const leads = parseInt(c.leads || "0", 10);
                    const schedules = parseInt(c.schedules || "0", 10);
                    const cpl = leads > 0 ? spend / leads : 0;
                    const cps = schedules > 0 ? spend / schedules : 0;
                    const cplStatus = leads > 0 ? costStatus(cpl, targets?.kpiCostPerLead) : null;
                    const cpsStatus =
                      schedules > 0 ? costStatus(cps, targets?.kpiCostPerCall) : null;
                    return (
                      <tr
                        key={c.campaignId}
                        className="border-t border-white/[0.06] hover:bg-[#0a0c10]/40 transition-colors"
                      >
                        <td
                          className="p-2 text-white font-medium max-w-[200px] truncate"
                          title={c.campaignName}
                        >
                          {c.campaignName}
                        </td>
                        <td className="p-2 text-right text-blue-400">
                          {formatCurrency(spend, currency)}
                        </td>
                        <td className="p-2 text-right text-emerald-400">
                          {leads > 0 ? formatNumber(leads) : "—"}
                        </td>
                        <td
                          className={`p-2 text-right font-medium ${cplStatus === "ok" ? "text-emerald-400" : cplStatus === "bad" ? "text-red-400" : "text-red-400"}`}
                        >
                          {leads > 0 ? formatCurrency(cpl, currency) : "—"}
                        </td>
                        <td className="p-2 text-right text-amber-400">
                          {schedules > 0 ? formatNumber(schedules) : "—"}
                        </td>
                        <td
                          className={`p-2 text-right font-medium ${cpsStatus === "ok" ? "text-emerald-400" : cpsStatus === "bad" ? "text-red-400" : "text-rose-400"}`}
                        >
                          {schedules > 0 ? formatCurrency(cps, currency) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Totals row */}
                <tfoot>
                  <tr className="border-t-2 border-white/[0.1] bg-[#0a0c10]/60 font-semibold">
                    <td className="p-2 text-white/70">{t("kpi.total")}</td>
                    <td className="p-2 text-right text-blue-300">
                      {formatCurrency(totalSpend, currency)}
                    </td>
                    <td className="p-2 text-right text-emerald-300">
                      {totalLeads > 0 ? formatNumber(totalLeads) : "—"}
                    </td>
                    <td className="p-2 text-right text-red-300">
                      {totalLeads > 0 ? formatCurrency(costPerLead, currency) : "—"}
                    </td>
                    <td className="p-2 text-right text-amber-300">
                      {totalSchedules > 0 ? formatNumber(totalSchedules) : "—"}
                    </td>
                    <td className="p-2 text-right text-rose-300">
                      {totalSchedules > 0 ? formatCurrency(costPerSchedule, currency) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Facebook Logo SVG ----
const MetaLogo = () => (
  <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center">
    <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  </div>
);

interface AnalyticsSettingsData {
  selectedAdAccountIds: string[];
  dataSources: { meta: boolean; ghl: boolean };
  kpiCostPerLead: string | null;
  kpiCostPerCall: string | null;
  kpiCostPerClient: string | null;
  kpiTargetRoas: string | null;
  accountConfigs: Record<
    string,
    {
      kpiCostPerLead?: string | null;
      kpiCostPerCall?: string | null;
      kpiCostPerClient?: string | null;
      kpiTargetRoas?: string | null;
      conversionLead?: string | null;
      conversionCall?: string | null;
      conversionSale?: string | null;
    }
  >;
}

export default function MetaConnectPanel({ onInsightsLoaded, forUserId }: MetaConnectPanelProps) {
  const { user } = useAuth();
  // VP-116: rozwiązany serwerowo dostęp do Performance (patrz /api/auth/me).
  const hasPerformance = (user as { hasPerformance?: boolean } | null)?.hasPerformance !== false;
  const queryClient = useQueryClient();
  const { t } = useTranslation("metaPanel");
  const { toast } = useToast();
  const [datePreset, setDatePreset] = useState("last_30d");
  const datePresets = useMemo(() => buildDatePresets(t), [t]);
  const [insightsData, setInsightsData] = useState<MetaInsightsData | null>(null);
  // Multi-account: store insights per account
  const [multiAccountInsights, setMultiAccountInsights] = useState<
    Record<string, MetaInsightsData>
  >({});
  const [isFetchingMulti, setIsFetchingMulti] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  // Check connection status
  const { data: status, isLoading: statusLoading } = useQuery<MetaStatus>({
    queryKey: ["meta-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/meta/status${fuParam}`),
    staleTime: 30_000,
  });

  // Fetch analytics settings (to pre-populate selected accounts + KPI targets)
  // VP-116: /api/analytics-settings to funkcja Performance i zwraca Organicowi 403.
  // Karta Meta jest dla Organica kanałem publikacji, więc musi działać bez tego —
  // cele KPI są tu i tak opcjonalne.
  const { data: analyticsSettings } = useQuery<AnalyticsSettingsData>({
    queryKey: ["analytics-settings"],
    queryFn: () => apiRequest("GET", "/api/analytics-settings"),
    staleTime: 60_000,
    enabled: hasPerformance,
  });

  // Listen for postMessage from OAuth popup callback
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "META_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: ["meta-integration-status"] });
      }
      if (event.data?.type === "META_OAUTH_ERROR") {
        // VP-311: wcześniej był tu tylko console.error — user widział znikające okno i nic więcej.
        toast({
          title: t("popup.connectionError"),
          description: t("popup.oauthErrorDescription", {
            error: String(event.data.error ?? ""),
          }),
          variant: "destructive",
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient, toast, t]);

  // Get resolved KPI targets for a specific account (per-account overrides > global defaults)
  const getTargetsForAccount = (accountId: string): KpiTargets => {
    const global: KpiTargets = {
      kpiCostPerLead: analyticsSettings?.kpiCostPerLead,
      kpiCostPerCall: analyticsSettings?.kpiCostPerCall,
      kpiCostPerClient: analyticsSettings?.kpiCostPerClient,
      kpiTargetRoas: analyticsSettings?.kpiTargetRoas,
    };
    const perAccount = analyticsSettings?.accountConfigs?.[accountId];
    if (!perAccount) return global;
    return {
      kpiCostPerLead: perAccount.kpiCostPerLead || global.kpiCostPerLead,
      kpiCostPerCall: perAccount.kpiCostPerCall || global.kpiCostPerCall,
      kpiCostPerClient: perAccount.kpiCostPerClient || global.kpiCostPerClient,
      kpiTargetRoas: perAccount.kpiTargetRoas || global.kpiTargetRoas,
    };
  };

  // Global KPI targets (for combined view)
  const globalTargets: KpiTargets = {
    kpiCostPerLead: analyticsSettings?.kpiCostPerLead,
    kpiCostPerCall: analyticsSettings?.kpiCostPerCall,
    kpiCostPerClient: analyticsSettings?.kpiCostPerClient,
    kpiTargetRoas: analyticsSettings?.kpiTargetRoas,
  };

  // Selected account IDs from settings
  const selectedAccountIds = analyticsSettings?.selectedAdAccountIds || [];
  const hasSelectedAccounts = selectedAccountIds.length > 0;

  // Connect mutation – open blank popup synchronously to avoid popup blocker
  const popupRef = useRef<Window | null>(null);
  const connectMutation = useMutation({
    mutationFn: () => apiRequest("GET", "/api/integrations/meta/connect"),
    onSuccess: (data: { url: string }) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.location.href = data.url;
      }
    },
    onError: (err) => {
      // Show error in popup instead of silently closing
      if (popupRef.current && !popupRef.current.closed) {
        try {
          popupRef.current.document.body.innerHTML = `<div style="padding:20px;font-family:sans-serif;color:#333"><h3>${escapeHtml(t("popup.connectionError"))}</h3><p>${escapeHtml(t("popup.tryAgain"))}</p></div>`;
        } catch {
          popupRef.current.close();
        }
      }
    },
  });
  const handleMetaConnect = () => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    popupRef.current = window.open(
      "about:blank",
      "meta_oauth",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    // Write loading page immediately so browser doesn't auto-close blank popup
    if (popupRef.current) {
      popupRef.current.document.write(
        `<html><head><title>Meta Ads</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>${escapeHtml(t("popup.loading"))}</p></body></html>`
      );
      popupRef.current.document.close();
    }
    const checkInterval = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(checkInterval);
        queryClient.invalidateQueries({ queryKey: ["meta-integration-status"] });
      }
    }, 500);
    connectMutation.mutate();
  };

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/meta/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-integration-status"] });
      setInsightsData(null);
      setMultiAccountInsights({});
    },
  });

  // Select ad account mutation (for single-account mode fallback)
  const selectAccountMutation = useMutation({
    mutationFn: (acc: { adAccountId: string; adAccountName: string }) =>
      apiRequest("POST", "/api/integrations/meta/select-ad-account", acc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-integration-status"] });
    },
  });

  // Fetch insights for single account
  const insightsMutation = useMutation({
    mutationFn: (preset: string) =>
      apiRequest("POST", "/api/integrations/meta/insights", { datePreset: preset }),
    onSuccess: (data: MetaInsightsData) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  // Fetch all selected accounts at once
  const fetchAllAccounts = async () => {
    const accountsToFetch = hasSelectedAccounts
      ? selectedAccountIds
      : status?.selectedAdAccountId
        ? [status.selectedAdAccountId]
        : [];
    if (accountsToFetch.length === 0) return;

    setIsFetchingMulti(true);
    setMultiAccountInsights({});
    setInsightsData(null);
    const results: Record<string, MetaInsightsData> = {};
    const contextParts: string[] = [];

    for (const accountId of accountsToFetch) {
      try {
        const data: MetaInsightsData = await apiRequest("POST", "/api/integrations/meta/insights", {
          datePreset,
          adAccountId: accountId,
        });
        results[accountId] = data;
        contextParts.push(`[${data.adAccountName}]\n${data.context}`);
      } catch (err) {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error(`[Multi] Error fetching ${accountId}:`, err);
      }
    }

    setMultiAccountInsights(results);
    setIsFetchingMulti(false);
    if (contextParts.length > 0) {
      onInsightsLoaded?.(contextParts.join("\n\n---\n\n"));
    }
  };

  // Derive currency from selected ad account
  const selectedCurrency =
    status?.adAccounts?.find((a) => a.id === status?.selectedAdAccountId)?.currency || "PLN";

  if (statusLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-white/60">{t("checkingConnection")}</span>
      </div>
    );
  }

  // ----- NOT CONNECTED -----
  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MetaLogo />
            <span className="text-sm font-medium text-white">Meta Ads</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("notConnected")}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={handleMetaConnect}
            disabled={connectMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connecting")}
              </>
            ) : (
              t("connect")
            )}
          </Button>
        </div>
        <p className="text-xs text-white/40">{t("connectDesc")}</p>
      </div>
    );
  }

  // ----- CONNECTED -----
  const adAccounts = status.adAccounts || [];
  const hasSelectedAccount = !!status.selectedAdAccountId;
  const multiMode = hasSelectedAccounts && selectedAccountIds.length > 0;
  const hasMultiResults = Object.keys(multiAccountInsights).length > 0;

  return (
    <div className="p-3 bg-white/[0.04] border border-blue-500/30 rounded-lg space-y-3">
      {/* Header: connection status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MetaLogo />
          <span className="text-sm font-medium text-white">Meta Ads</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {status.assignedByAdmin
              ? t("assignedByAdmin")
              : t("connected", { name: status.metaUserName })}
          </Badge>
        </div>
        {!status.assignedByAdmin && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            {t("disconnect")}
          </Button>
        )}
      </div>

      {/* Ad account selector (single mode - when no accounts configured in settings) */}
      {!multiMode && adAccounts.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/60 whitespace-nowrap">{t("accountsLabel")}</span>
          <Select
            value={status.selectedAdAccountId || ""}
            onValueChange={(val) => {
              const acc = adAccounts.find((a) => a.id === val);
              if (acc)
                selectAccountMutation.mutate({ adAccountId: acc.id, adAccountName: acc.name });
            }}
          >
            <SelectTrigger className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
              <SelectValue placeholder={t("accountSelector")} />
            </SelectTrigger>
            <SelectContent>
              {adAccounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} ({a.id}) – {a.currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!multiMode && adAccounts.length === 1 && (
        <div className="text-xs text-white/60">
          {t("accountLabel", { name: adAccounts[0].name, currency: adAccounts[0].currency })}
        </div>
      )}

      {/* Multi-mode: show which accounts are configured */}
      {multiMode && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-white/60">{t("accountsLabel")}</span>
          {selectedAccountIds.map((id) => {
            const acc = adAccounts.find((a) => a.id === id);
            return acc ? (
              <Badge key={id} className="text-[10px] bg-red-500/15 text-red-300 border-red-500/30">
                {acc.name}
              </Badge>
            ) : null;
          })}
        </div>
      )}

      {/* Fetch controls */}
      <div className="flex items-center gap-2">
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {datePresets.map((p: { value: string; label: string }) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={fetchAllAccounts}
          disabled={isFetchingMulti || insightsMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
        >
          {isFetchingMulti ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("loadingData")}
            </>
          ) : multiMode ? (
            t("fetchDataMulti", { count: selectedAccountIds.length })
          ) : (
            t("fetchData")
          )}
        </Button>
      </div>

      {/* ═══ REPORTS ═══ */}
      {hasMultiResults && (
        <div className="space-y-4">
          {/* Combined totals (if multiple accounts) */}
          {Object.keys(multiAccountInsights).length > 1 &&
            (() => {
              const allCampaigns = Object.values(multiAccountInsights).flatMap((d) => d.campaigns);
              if (allCampaigns.length === 0) return null;
              return (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-px flex-1 bg-red-500/30" />
                    <span className="text-[10px] text-red-400 font-semibold uppercase tracking-widest">
                      {t("combinedLabel", { count: Object.keys(multiAccountInsights).length })}
                    </span>
                    <div className="h-px flex-1 bg-red-500/30" />
                  </div>
                  <KpiDashboard campaigns={allCampaigns} currency="PLN" targets={globalTargets} />
                </div>
              );
            })()}

          {/* Per-account breakdown */}
          {Object.entries(multiAccountInsights).map(([accountId, data]) => {
            const accMeta = adAccounts.find((a) => a.id === accountId);
            const accCurrency = accMeta?.currency || "PLN";
            const accTargets = getTargetsForAccount(accountId);

            return (
              <div
                key={accountId}
                className="border border-white/[0.08] rounded-lg overflow-hidden"
              >
                <div className="bg-[#0a0c10]/80 px-3 py-2 flex items-center gap-2">
                  <MetaLogo />
                  <span className="text-xs font-medium text-white">{data.adAccountName}</span>
                  <Badge className="text-[10px] bg-white/[0.08] text-white/70 border-white/[0.1]">
                    {accCurrency}
                  </Badge>
                  <Badge className="text-[10px] bg-blue-600/20 text-blue-400 border-blue-500/50 ml-auto">
                    {t("campaignsCount", { count: data.campaigns.length })}
                  </Badge>
                </div>
                <div className="p-2">
                  {data.campaigns.length > 0 ? (
                    <KpiDashboard
                      campaigns={data.campaigns}
                      currency={accCurrency}
                      targets={accTargets}
                    />
                  ) : (
                    <p className="text-xs text-white/40 text-center py-2">{t("noCampaigns")}</p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Status */}
          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">
              {t("dataLoadedDesc", { count: Object.keys(multiAccountInsights).length })}
            </span>
          </div>
        </div>
      )}

      {/* Single-account fallback results (when no multi results) */}
      {!hasMultiResults && insightsData && insightsData.campaigns.length > 0 && (
        <KpiDashboard
          campaigns={insightsData.campaigns}
          currency={selectedCurrency}
          targets={globalTargets}
        />
      )}

      {!hasMultiResults && insightsData && insightsData.campaigns.length === 0 && (
        <div className="text-center py-4">
          <p className="text-xs text-white/40">{t("noCampaignsData")}</p>
        </div>
      )}

      {insightsMutation.isError && <p className="text-xs text-amber-400/70">{t("retryFetch")}</p>}
    </div>
  );
}
